import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Table, Form, InputGroup, Button, Dropdown } from 'react-bootstrap';
import { FaSearch, FaTimes, FaRegCopy, FaCheck } from 'react-icons/fa';
import MHistoryModal from './modals/MatchHistoryModal';
import { fetchMatchHistory, fetchAvailableSports } from './Api';

function MHistory() {
  const [matches, setMatches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterSport, setFilterSport] = useState('All Sports');
  const [availableSports, setAvailableSports] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const limit = 10;

  const tableContainerRef = useRef(null);

  const copyToClipboard = (id) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Modern API
      navigator.clipboard.writeText(id)
        .then(() => {
          setCopiedId(id);
          setTimeout(() => setCopiedId(null), 2000);
        })
        .catch((err) => {
          console.error('Failed to copy:', err);
        });
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = id;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textarea);
    }
  };


  const handleSportClick = (match) => {
    setSelectedMatch(match);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const getScore = (match) => {
    const team1Score = match.teams.team_1?.filter(m => m.score)?.length || 0;
    const team2Score = match.teams.team_2?.filter(m => m.score)?.length || 0;
    return `${team1Score}:${team2Score}`;
  };

  // Load available sports
  useEffect(() => {
    const loadSports = async () => {
      try {
        const data = await fetchAvailableSports();
        setAvailableSports(data);
      } catch (error) {
        console.error('Error fetching sports:', error);
      }
    };
    loadSports();
  }, []);

  // Fetch matches by page
  // Fetch matches by page
  const fetchMatches = async (page = 1, limit = 10) => {
    if (isFetching) return;
    setIsFetching(true);

    try {
      const data = await fetchMatchHistory(page, limit);

      setTotalPages(data.total_pages);



      setMatches(prev => {
        const combined = [...prev, ...data.matches];
        // Ensure unique matches by match_id
        const uniqueMatches = Array.from(
          new Map(combined.map(m => [m.match_id, m])).values()
        );
        return uniqueMatches;
      });
    } catch (err) {
      console.error("Error fetching matches:", err);
    } finally {
      setIsFetching(false);
    }
  };


  const isFetchingRef = useRef(false);  // top-level, outside useEffect
  const nextPageRef = useRef(1);        // top-level, outside useEffect

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 50;
      if (!atBottom || isFetchingRef.current || nextPageRef.current > totalPages) return;

      isFetchingRef.current = true;       // immediately block further fetches
      fetchMatches(nextPageRef.current, limit).finally(() => {
        isFetchingRef.current = false;    // release block after fetch
      });
      nextPageRef.current += 1;           // increment page immediately
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [totalPages]);  // remove isFetching from dependencies be



  // Filter matches
  const filteredMatches = matches.filter((match) => {
    const matchesSearch =
      match.sport_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      match.match_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      match.player?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDate =
      filterDate === '' ||
      new Date(match.date).toISOString().slice(0, 10) === filterDate;

    const matchesSport =
      filterSport === 'All Sports' ||
      match.sport_name.toLowerCase() === filterSport.toLowerCase();

    return matchesSearch && matchesDate && matchesSport;
  });

  useEffect(() => {
    fetchMatches(1, limit).then(() => {
      nextPageRef.current = 2; // start from next page
    });
  }, []);



  return (
    <div
      className="p-4 w-100"
      style={{
        backgroundColor: '#ff9b45',
        color: '#FFECC8',
        minHeight: '100%',
        boxShadow: '0 0 10px rgba(0,0,0,0.05)',
      }}
    >
      <h5 className="mb-4 fw-bold">Match History</h5>

      <div className="d-flex mb-3 gap-2">
        <input
          type="text"
          className="form-control"
          placeholder="Search matches..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="form-select"
          value={filterSport}
          onChange={(e) => setFilterSport(e.target.value)}
          style={{ maxWidth: 180 }}
        >
          <option>All Sports</option>
          {availableSports.map((sport) => (
            <option key={sport.id} value={sport.display_name}>
              {sport.display_name}
            </option>
          ))}
        </select>
        <input
          type="date"
          className="form-control"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          style={{ maxWidth: 180 }}
        />
        <Button
          variant="outline-secondary"
          style={{
            border: '1px solid #FFECC8',
            color: '#FFECC8',
            backgroundColor: 'transparent',
            minWidth: 40,
          }}
          onClick={() => {
            setFilterDate('');
            setFilterSport('All Sports');
            setSearchTerm('');
          }}
        >
          <FaTimes size={18} />
        </Button>
      </div>

      <div
        ref={tableContainerRef}
        className="table-responsive"
        style={{
          maxHeight: 'calc(10 * 2.5rem + 1rem)',
          overflowY: filteredMatches.length > 10 ? 'auto' : 'visible',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '4px',
        }}
      >
        <table className="table table-bordered table-hover mb-0">
          <thead className="table-light">
            <tr>
              <th>Match ID</th>
              <th>Sport Name</th>
              <th>Player</th>
              <th>Score</th>
              <th>Date</th>
              <th>Counted</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredMatches.map((match) => (
              <tr
                key={match.match_id}
                onClick={() => handleSportClick(match)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: match.is_counted ? 'transparent' : 'rgba(255,0,0,0.2)',
                  color: '#FFD580',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,236,200,0.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = match.is_counted ? 'transparent' : 'rgba(255,0,0,0.2)')}
              >
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{match.match_id}</span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(match.match_id);
                      }}
                    >
                      {copiedId === match.match_id ? <FaCheck color="green" /> : <FaRegCopy color="black" />}
                    </Button>
                  </div>
                </td>
                <td>{match.sport_name}</td>
                <td>{match.player}</td>
                <td>{getScore(match)}</td>
                <td>{new Date(match.date).toLocaleDateString()}</td>
                <td>{match.is_counted ? 'Yes' : 'No'}</td>
                <td>{match.status}</td>
              </tr>
            ))}
            {filteredMatches.length === 0 && (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                  No matches found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {isFetching && (
          <div style={{ textAlign: 'center', padding: '10px', color: '#FFD580' }}>
            Loading more matches...
          </div>
        )}
      </div>

      <MHistoryModal show={showModal} onHide={handleCloseModal} match={selectedMatch} />
    </div>
  );
}

export default MHistory;
